// /frontend/src/pages/admin/AdminOrderDetail.jsx
import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import AdminLayout from '../../components/layout/AdminLayout';
import { FaArrowLeft, FaTruck, FaRegClock, FaCheck, FaPrint, FaFileInvoice } from 'react-icons/fa';
import { orderService } from '../../services/orderService';
import { toast } from 'react-toastify';

// Helper function to convert base64 to blob for PDFs
const b64toBlob = (b64Data, contentType = '', sliceSize = 512) => {
  const byteCharacters = atob(b64Data);
  const byteArrays = [];

  for (let offset = 0; offset < byteCharacters.length; offset += sliceSize) {
    const slice = byteCharacters.slice(offset, offset + sliceSize);
    const byteNumbers = new Array(slice.length);
    
    for (let i = 0; i < slice.length; i++) {
      byteNumbers[i] = slice.charCodeAt(i);
    }
    
    const byteArray = new Uint8Array(byteNumbers);
    byteArrays.push(byteArray);
  }

  return new Blob(byteArrays, { type: contentType });
};

const AdminOrderDetail = () => {
  const { id } = useParams();
  const [order, setOrder] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showStatusDropdown, setShowStatusDropdown] = useState(false);
  
  // Status options
  const statuses = [
    'pending',
    'processing',
    'shipped',
    'delivered',
    'cancelled'
  ];
  
  // Fetch order details
  useEffect(() => {
    const fetchOrderDetails = async () => {
      try {
        setLoading(true);
        // Replace with your actual API call when implemented
        const data = await orderService.getOrderById(id);
        
        console.log('Received order data:', data);
        
        // We expect the actual order data to be nested under 'order' property
        // Handle both direct object and nested object formats
        const orderData = data.order ? data.order : data;
        
        // Set the order state
        setOrder(orderData);
        setLoading(false);
      } catch (err) {
        console.error('Error fetching order details:', err);
        setError(err.message || 'Failed to fetch order details');
        setLoading(false);
      }
    };
    
    fetchOrderDetails();
  }, [id]);
  
  // Function to view PDF from base64 data
  const viewPdf = (data, contentType) => {
    try {
      const blob = b64toBlob(data, contentType);
      const blobUrl = URL.createObjectURL(blob);
      window.open(blobUrl, '_blank');
    } catch (error) {
      console.error('Error opening PDF:', error);
      toast.error('Error opening PDF');
    }
  };
  
  // Handle update order status
  const handleUpdateStatus = async (newStatus) => {
    try {
      // Replace with your actual API call when implemented
      await orderService.updateOrderStatus(id, { status: newStatus });
      
      // Update order status in the state
      setOrder({
        ...order,
        status: newStatus
      });
      
      toast.success('Order status updated successfully');
      setShowStatusDropdown(false);
      
      // Reload the page after a short delay to ensure the updated data is shown
      setTimeout(() => window.location.reload(), 1500);
    } catch (err) {
      toast.error(`Failed to update order status: ${err.message}`);
    }
  };
  
  // Get status badge style
  const getStatusBadgeStyle = (status) => {
    if (!status) return 'bg-gray-100 text-gray-800';
    
    switch (status) {
      case 'pending':
        return 'bg-yellow-100 text-yellow-800';
      case 'processing':
        return 'bg-blue-100 text-blue-800';
      case 'shipped':
        return 'bg-indigo-100 text-indigo-800';
      case 'delivered':
        return 'bg-green-100 text-green-800';
      case 'cancelled':
        return 'bg-red-100 text-red-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };
  
  // Get status icon
  const getStatusIcon = (status) => {
    if (!status) return null;
    
    switch (status) {
      case 'pending':
        return <FaRegClock className="mr-1" />;
      case 'processing':
        return <FaRegClock className="mr-1" />;
      case 'shipped':
        return <FaTruck className="mr-1" />;
      case 'delivered':
        return <FaCheck className="mr-1" />;
      default:
        return null;
    }
  };
  
  // Format status text
  const formatStatusText = (status) => {
    if (!status) return 'Unknown';
    return status.charAt(0).toUpperCase() + status.slice(1);
  };
  
  if (loading) {
    return (
      <AdminLayout>
        <div className="flex justify-center items-center h-96">
          <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-teal-800"></div>
        </div>
      </AdminLayout>
    );
  }
  
  if (error) {
    return (
      <AdminLayout>
        <div className="bg-red-50 border border-red-200 text-red-800 p-4 rounded-md mb-6">
          <p>{error}</p>
          <button 
            onClick={() => window.location.reload()}
            className="mt-2 px-4 py-2 bg-red-600 text-white rounded-md hover:bg-red-700"
          >
            Try Again
          </button>
        </div>
      </AdminLayout>
    );
  }
  
  if (!order) {
    return (
      <AdminLayout>
        <div className="bg-yellow-50 border border-yellow-200 text-yellow-800 p-4 rounded-md mb-6">
          <p>Order not found.</p>
          <Link 
            to="/admin/orders"
            className="mt-2 inline-block px-4 py-2 bg-teal-600 text-white rounded-md hover:bg-teal-700"
          >
            Back to Orders
          </Link>
        </div>
      </AdminLayout>
    );
  }
  
  return (
    <AdminLayout>
      {/* Header */}
      <div className="flex flex-wrap justify-between items-center mb-6">
        <div className="flex items-center mb-4 md:mb-0">
          <Link to="/admin/orders" className="mr-4 text-gray-500 hover:text-gray-700">
            <FaArrowLeft />
          </Link>
          <h1 className="text-2xl font-semibold text-gray-800">
            Order #{order._id || id}
          </h1>
          <span className={`ml-4 inline-flex items-center px-3 py-1 text-sm font-medium rounded-full ${getStatusBadgeStyle(order.status)}`}>
            {getStatusIcon(order.status)}
            {formatStatusText(order.status)}
          </span>
        </div>
        <div className="flex space-x-3">
          <button 
            onClick={() => window.print()}
            className="flex items-center px-4 py-2 border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50"
          >
            <FaPrint className="mr-2" />
            Print
          </button>
          <button 
            className="flex items-center px-4 py-2 border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50"
          >
            <FaFileInvoice className="mr-2" />
            Download Invoice
          </button>
          <div className="relative inline-block text-left">
            <button 
              className="flex items-center px-4 py-2 bg-teal-600 text-white rounded-md hover:bg-teal-700"
              onClick={() => setShowStatusDropdown(!showStatusDropdown)}
            >
              <FaTruck className="mr-2" />
              Update Status
            </button>
            
            {/* Status dropdown */}
            {showStatusDropdown && (
              <div className="origin-top-right absolute right-0 mt-2 w-48 rounded-md shadow-lg bg-white ring-1 ring-black ring-opacity-5 focus:outline-none z-10">
                <div className="py-1">
                  {statuses.map((status) => (
                    <button
                      key={status}
                      onClick={() => handleUpdateStatus(status)}
                      className={`${
                        order.status === status ? 'bg-gray-100' : ''
                      } block w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-100`}
                      disabled={order.status === status}
                    >
                      {status.charAt(0).toUpperCase() + status.slice(1)}
                    </button>
                  ))}
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
      
      {/* Order Information */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-6">
        {/* Customer Info */}
        <div className="bg-white rounded-lg shadow-md p-6">
          <h2 className="text-lg font-medium text-gray-800 mb-4">Customer Information</h2>
          <div className="space-y-3">
            <div>
              <h3 className="text-sm font-medium text-gray-500">Customer</h3>
              <p className="text-gray-800">
                {order.user?.fullName || 
                 (order.user?.firstName && order.user?.lastName ? 
                  `${order.user.firstName} ${order.user.lastName}` : 
                  order.shippingAddress?.fullName || 'N/A')}
              </p>
            </div>
            <div>
              <h3 className="text-sm font-medium text-gray-500">Email</h3>
              <p className="text-gray-800">{order.user?.email || 'N/A'}</p>
            </div>
            <div>
              <h3 className="text-sm font-medium text-gray-500">Phone</h3>
              <p className="text-gray-800">{order.shippingAddress?.phone || 'N/A'}</p>
            </div>
          </div>
        </div>
        
        {/* Shipping Info */}
        <div className="bg-white rounded-lg shadow-md p-6">
          <h2 className="text-lg font-medium text-gray-800 mb-4">Shipping Information</h2>
          <div className="space-y-3">
            <div>
              <h3 className="text-sm font-medium text-gray-500">Address</h3>
              <p className="text-gray-800">{order.shippingAddress?.address || 'N/A'}</p>
            </div>
            <div>
              <h3 className="text-sm font-medium text-gray-500">City, State, ZIP</h3>
              <p className="text-gray-800">
                {order.shippingAddress?.city || 'N/A'}, {order.shippingAddress?.state || ''} {order.shippingAddress?.postalCode || ''}
              </p>
            </div>
            <div>
              <h3 className="text-sm font-medium text-gray-500">Country</h3>
              <p className="text-gray-800">{order.shippingAddress?.country || 'N/A'}</p>
            </div>
            <div>
              <h3 className="text-sm font-medium text-gray-500">Shipping Method</h3>
              <p className="text-gray-800">{order.shippingMethod || 'Standard Shipping'}</p>
            </div>
          </div>
        </div>
        
        {/* Order Summary */}
        <div className="bg-white rounded-lg shadow-md p-6">
          <h2 className="text-lg font-medium text-gray-800 mb-4">Order Summary</h2>
          <div className="space-y-3">
            <div>
              <h3 className="text-sm font-medium text-gray-500">Date Placed</h3>
              <p className="text-gray-800">
                {order.createdAt ? new Date(order.createdAt).toLocaleString() : 'N/A'}
              </p>
            </div>
            <div>
              <h3 className="text-sm font-medium text-gray-500">Payment Method</h3>
              <p className="text-gray-800">
                {order.paymentMethod ? 
                  (typeof order.paymentMethod === 'string' ? 
                    order.paymentMethod.replace(/_/g, ' ').charAt(0).toUpperCase() + order.paymentMethod.replace(/_/g, ' ').slice(1) : 
                    'N/A') : 
                  'N/A'}
              </p>
            </div>
            <div>
              <h3 className="text-sm font-medium text-gray-500">Payment Status</h3>
              <p className={`font-medium ${
                order.paymentStatus === 'paid' || order.isPaid ? 'text-green-600' : 
                order.paymentStatus === 'rejected' ? 'text-red-600' :
                'text-yellow-600'
              }`}>
                {order.paymentStatus ? formatStatusText(order.paymentStatus) : (order.isPaid ? 'Paid' : 'Pending')}
              </p>
              {order.isPaid && order.paidAt && (
                <p className="text-xs text-gray-500">
                  {new Date(order.paidAt).toLocaleString()}
                </p>
              )}
            </div>
            <div>
              <h3 className="text-sm font-medium text-gray-500">Total Amount</h3>
              <p className="text-xl font-medium text-teal-600">
                ${(order.totalPrice || 0).toFixed(2)}
              </p>
            </div>
          </div>
        </div>
      </div>
      
      {/* Order Items */}
      <div className="bg-white rounded-lg shadow-md overflow-hidden mb-6">
        <div className="px-6 py-4 border-b border-gray-200">
          <h2 className="text-lg font-medium text-gray-800">Order Items</h2>
        </div>
        
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Product
                </th>
                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Price
                </th>
                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Quantity
                </th>
                <th scope="col" className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Total
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {(order.orderItems || []).map((item, index) => (
                <tr key={index} className="hover:bg-gray-50">
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="flex items-center">
                      <div className="h-10 w-10 flex-shrink-0">
                        <img 
                          className="h-10 w-10 rounded-md object-cover" 
                          src={item.image || 'https://via.placeholder.com/150'} 
                          alt={item.name}
                          onError={(e) => {
                            e.target.onerror = null;
                            e.target.src = 'https://via.placeholder.com/150';
                          }}
                        />
                      </div>
                      <div className="ml-4">
                        <div className="text-sm font-medium text-gray-900">{item.name}</div>
                        {item.variants && Object.keys(item.variants).length > 0 && (
                          <div className="text-sm text-gray-500">
                            {Object.entries(item.variants).map(([key, value], idx) => (
                              <div key={idx}>{key}: {value}</div>
                            ))}
                          </div>
                        )}
                      </div>
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="text-sm text-gray-900">${(item.price || 0).toFixed(2)}</div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="text-sm text-gray-900">{item.quantity}</div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-right">
                    <div className="text-sm font-medium text-gray-900">
                      ${((item.price || 0) * (item.quantity || 1)).toFixed(2)}
                    </div>
                  </td>
                </tr>
              ))}
              {order.orderItems?.length === 0 && (
                <tr>
                  <td colSpan="4" className="px-6 py-4 text-center text-sm text-gray-500">
                    No items found in this order.
                  </td>
                </tr>
              )}
            </tbody>
            <tfoot className="bg-gray-50">
              <tr>
                <td colSpan="3" className="px-6 py-4 text-right text-sm font-medium text-gray-500">
                  Subtotal
                </td>
                <td className="px-6 py-4 text-right text-sm font-medium text-gray-900">
                  ${(order.itemsPrice || 0).toFixed(2)}
                </td>
              </tr>
              <tr>
                <td colSpan="3" className="px-6 py-4 text-right text-sm font-medium text-gray-500">
                  Shipping
                </td>
                <td className="px-6 py-4 text-right text-sm font-medium text-gray-900">
                  ${(order.shippingPrice || 0).toFixed(2)}
                </td>
              </tr>
              <tr>
                <td colSpan="3" className="px-6 py-4 text-right text-sm font-medium text-gray-500">
                  Tax
                </td>
                <td className="px-6 py-4 text-right text-sm font-medium text-gray-900">
                  ${(order.taxPrice || 0).toFixed(2)}
                </td>
              </tr>
              {(order.discount > 0) && (
                <tr>
                  <td colSpan="3" className="px-6 py-4 text-right text-sm font-medium text-gray-500">
                    Discount
                  </td>
                  <td className="px-6 py-4 text-right text-sm font-medium text-red-600">
                    -${(order.discount || 0).toFixed(2)}
                  </td>
                </tr>
              )}
              <tr>
                <td colSpan="3" className="px-6 py-4 text-right text-sm font-medium text-gray-900">
                  Total
                </td>
                <td className="px-6 py-4 text-right text-base font-medium text-teal-600">
                  ${(order.totalPrice || 0).toFixed(2)}
                </td>
              </tr>
            </tfoot>
          </table>
        </div>
      </div>
      
      {/* Payment Evidence Section (New) */}
      {order.paymentEvidence && (
        <div className="bg-white rounded-lg shadow-md overflow-hidden mb-6">
          <div className="px-6 py-4 border-b border-gray-200">
            <h2 className="text-lg font-medium text-gray-800">Payment Evidence</h2>
          </div>
          <div className="p-6">
            {order.paymentEvidence.contentType && order.paymentEvidence.contentType.startsWith('image/') ? (
              <div className="flex justify-center">
                <img 
                  src={`data:${order.paymentEvidence.contentType};base64,${order.paymentEvidence.data}`}
                  alt="Payment Evidence"
                  className="max-w-full h-auto rounded border"
                  style={{ maxHeight: '300px' }}
                />
              </div>
            ) : order.paymentEvidence.contentType === 'application/pdf' ? (
              <div className="flex justify-center">
                <button
                  onClick={() => viewPdf(order.paymentEvidence.data, order.paymentEvidence.contentType)}
                  className="px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700"
                >
                  View PDF Document
                </button>
              </div>
            ) : (
              <p className="text-center text-gray-600">Payment evidence uploaded (format not displayable)</p>
            )}
            
            {order.paymentEvidenceUploadedAt && (
              <p className="mt-4 text-sm text-center text-gray-500">
                Uploaded: {new Date(order.paymentEvidenceUploadedAt).toLocaleString()}
              </p>
            )}
            
            <div className="mt-6 flex justify-center space-x-4">
              {/* Only show payment approval/rejection buttons when payment status is 'pending' */}
              {order.paymentStatus === 'pending' && (
                <>
                  <button
                    onClick={async () => {
                      try {
                        await orderService.verifyPayment(order._id, {
                          status: 'paid',
                          notes: 'Approved by admin'
                        });
                        toast.success('Payment approved successfully');
                        setTimeout(() => window.location.reload(), 1500);
                      } catch (err) {
                        toast.error(`Failed to approve payment: ${err.message}`);
                      }
                    }}
                    className="px-4 py-2 bg-green-600 text-white rounded hover:bg-green-700"
                  >
                    Approve Payment
                  </button>
                  
                  <button
                    onClick={async () => {
                      try {
                        await orderService.verifyPayment(order._id, {
                          status: 'rejected',
                          notes: 'Rejected by admin'
                        });
                        toast.success('Payment rejected successfully');
                        setTimeout(() => window.location.reload(), 1500);
                      } catch (err) {
                        toast.error(`Failed to reject payment: ${err.message}`);
                      }
                    }}
                    className="px-4 py-2 bg-red-600 text-white rounded hover:bg-red-700"
                  >
                    Reject Payment
                  </button>
                </>
              )}
              
              {/* Show payment status message when already decided */}
              {order.paymentStatus === 'paid' && (
                <div className="bg-green-100 text-green-800 px-4 py-2 rounded">
                  Payment has been approved
                </div>
              )}
              
              {order.paymentStatus === 'rejected' && (
                <div className="bg-red-100 text-red-800 px-4 py-2 rounded">
                  Payment has been rejected
                </div>
              )}
            </div>
          </div>
        </div>
      )}
      
      {/* Order History */}
      {order.history && order.history.length > 0 && (
        <div className="bg-white rounded-lg shadow-md overflow-hidden">
          <div className="px-6 py-4 border-b border-gray-200">
            <h2 className="text-lg font-medium text-gray-800">Order History</h2>
          </div>
          
          <div className="px-6 py-4">
            <ol className="relative border-l border-gray-200">
              {order.history.map((event, index) => (
                <li key={index} className="mb-6 ml-6">
                  <span className="absolute flex items-center justify-center w-6 h-6 bg-teal-100 rounded-full -left-3 ring-8 ring-white">
                    {event.type === 'status_update' ? (
                      <FaTruck className="w-3 h-3 text-teal-800" />
                    ) : (
                      <FaCheck className="w-3 h-3 text-teal-800" />
                    )}
                  </span>
                  <h3 className="flex items-center mb-1 text-sm font-semibold text-gray-900">
                    {event.title}
                    {index === 0 && (
                      <span className="bg-teal-100 text-teal-800 text-xs font-medium mr-2 px-2.5 py-0.5 rounded ml-3">
                        Latest
                      </span>
                    )}
                  </h3>
                  <time className="block mb-2 text-xs font-normal leading-none text-gray-400">
                    {event.date}
                  </time>
                  <p className="text-sm font-normal text-gray-500">
                    {event.description}
                  </p>
                </li>
              ))}
            </ol>
          </div>
        </div>
      )}
    </AdminLayout>
  );
};

export default AdminOrderDetail;